import React, {ReactElement, useEffect, useState} from "react";
import {Dialog} from "@headlessui/react";
import {FormattedMessage, useIntl} from "react-intl";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {ReactComponent as CloseIcon} from "components/Icons/close.svg";
import {SecondaryButton} from "components/UIComponents/SecondaryButton";
import {PrimaryButton} from "components/UIComponents/PrimaryButton";
import {menuEditorActions, menuEditorSelectors} from "./editorSlice";
import {FormSelect} from "components/UIComponents/FormSelect";
import {FormGroup} from "components/UIComponents/FormGroup";
import {FormInput} from "components/UIComponents/FormInput";
import {Checkbox} from "components/UIComponents/Checkbox";
import {assoc, isEmpty, trim} from "ramda";
import {Spinner} from "components/UIComponents/Spinner";
import {orderApi} from "lib/api/wineMenuApi";
import {toPdfRequestData} from "../pdf/generateWineMenuPdf";
import {CustomerAddressData, fetchCustomerAddressData} from "lib/api/wineAppApi";
import {authSelectors} from "../auth/authSlice";
import {ReactComponent as CheckedIcon} from "components/Icons/checked-colored.svg";
import { useParams } from "react-router";
import { menuManagementSelectors } from "features/menu/menumanagement/menuManagementSlice";
import { createDocumentHandler, createDocumentTypeHandler } from "./documentHandlerUtils";

export interface PrintModalProps {
}

type PrintDataLanguageType = 'de' | 'fr' | 'it' | 'en';

interface PrintData {
    numberOfPrints: number
    withLaminage: boolean
    restaurantName: string
    address: string
    zip: string
    city: string
    date: string
    comment: string
    additionalEmails: string
    language: PrintDataLanguageType
}

function emptyPrintData(): PrintData {
    return {
        numberOfPrints: 1,
        withLaminage: false,
        restaurantName: '',
        address: '',
        zip: '',
        city: '',
        date: '',
        comment: '',
        additionalEmails: '',
        language: 'de',
    };
}

function printDataFromAddressData(addressData: CustomerAddressData): PrintData {
    return {
        numberOfPrints: 1,
        withLaminage: false,
        restaurantName: addressData.name,
        address: addressData.street,
        zip: addressData.postalCode,
        city: addressData.city,
        date: '',
        comment: '',
        additionalEmails: '',
        language: 'de',
    };
}

function isEmailValid(email: string): boolean {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
}

export function PrintModal(props: PrintModalProps): ReactElement {
    const active = useAppSelector(menuEditorSelectors.printPopoverActive);
    const [data, setData] = useState<PrintData | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const [addressDataLoading, setAddressDataLoading] = useState<boolean>(false);
    const [printRequestLoading, setPrintRequestLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const isSalesUser = useAppSelector(authSelectors.isSalesUser);
    const emailRequired = isSalesUser;

    const document = useAppSelector(menuEditorSelectors.document);
    const type = document?.type ?? "wine";
    const docTypeHandler = createDocumentTypeHandler(type);
    const soldTo = useAppSelector(authSelectors.customer);

    useEffect(() => {
        if (data != null) {
            return;
        }

        if (soldTo == null) {
            setData(emptyPrintData());
            return;
        }

        setAddressDataLoading(true);
        fetchCustomerAddressData(soldTo)
            .then(addressData => {
                setData(printDataFromAddressData(addressData));
                setAddressDataLoading(false);
            })
            .catch(e => {
                setData(emptyPrintData());
                setAddressDataLoading(false);
            });
    }, [data, soldTo])

    const dispatch = useAppDispatch();
    const closeModal = () => dispatch(menuEditorActions.closePrintPopover());
    const intl = useIntl();

    let validEmailRequiredError = false;
    if (data && data.additionalEmails) {
        const invalidEmails = trim(data.additionalEmails)
            .split(',')
            .map(trim)
            .filter(Boolean)
            .filter(v=>!isEmailValid(v));
        validEmailRequiredError = invalidEmails.length !== 0;
    }

    const formFilled = data != null && [
        data.restaurantName,
        data.address,
        data.city,
        data.date,
        ...(emailRequired?[data.additionalEmails]:[])
    ].find(s => isEmpty(trim(s))) == null && !validEmailRequiredError;
   

    const sendPrintRequest = () => {
        if (document == null || data == null) {
            setError(true);
            return;
        }

        setPrintRequestLoading(true);
        setError(false);
        setSuccess(false);

        const pdfData = toPdfRequestData(document);
        
        orderApi.sendPrintOrder({
            order: {
                pdfData: pdfData as any, // todo add new a6 format to api
                printInfo: data
            }
        }).then((response: any) => {
            setPrintRequestLoading(false);
            if (!error) setSuccess(true);
        }).catch(error => {
            setPrintRequestLoading(false);
            setError(true);
        });
    };
    
    const [numberOfPrints, setNumberOfPrints] = useState<number[]>([]);

    useEffect(()=>{
        if (!document) return;
        const docHandler = createDocumentHandler(document);
        const numberOfPrints = docHandler.getNumberOfPrints();
        setNumberOfPrints(numberOfPrints);

        if (data?.numberOfPrints === 1 
            && numberOfPrints.length > 0 
            && !numberOfPrints.includes(1)
        ) {
            setData(assoc('numberOfPrints', numberOfPrints[0], data))
        }
    }, [document, data]);

    const menuId = useParams()['menuId'];
    const userId = useAppSelector(authSelectors.customer);
    const menuName = useAppSelector(menuManagementSelectors.menuName(userId, menuId));
    const customer = useAppSelector(authSelectors.customer);

    let additionalInfo: [string,string][] = [];
    const yes = intl.formatMessage({id:"printModal.additionalInfo.yes"}),
        no = intl.formatMessage({id:"printModal.additionalInfo.no"});

    if (document) {
        const tr = ([v0,v1]: (string|undefined|null)[]): [string,string] => ([
            intl.formatMessage({id:`printModal.additionalInfo.${v0}`}),
            v1||""
        ]);
        let pages = document.pages.length;
        if (document.coverPage) pages++;
        if (document.printMode === "brochure") {
            if (pages%4 > 0) {
                pages += 4-pages%4;
            }
        }
        additionalInfo = [
            ["name", menuName],
            ["customer", customer],
            ["paperSize", document.format.toUpperCase()],
            ["hasTitle", document.coverPage!==null?yes:no],
            ["printMode", intl.formatMessage({
                id: document.printMode==="brochure"
                ?"editor.format.brochure":"editor.format.singlePages"
            })],
            ["hasDegu", (document.degu || false)?yes:no],
            ["pages", pages.toString()]
        ].map(tr);
    }

    return (
        <Dialog
            open={active}
            onClose={closeModal}
            className="fixed z-modal inset-0 overflow-y-auto"
        >
            <div className="flex items-center justify-center min-h-screen">
                <Dialog.Overlay className="fixed inset-0 bg-text opacity-60"/>

                <div className={`relative flex flex-col w-full mx-auto
                                py-6 px-5 bg-white rounded-lg
                                ${success?'max-w-sm':'max-w-[46rem]'}
                                `}>

                    <div className="flex-none flex items-center pb-5">
                        <Dialog.Title className="flex-auto font-barlow text-lg font-semibold">
                            <FormattedMessage id={success?"editor.print.successEmail.title":"editor.print.title"} />
                        </Dialog.Title>
                        <button className="flex-none"
                                onClick={closeModal}>
                            <CloseIcon className="inline-block w-5 h-5" />
                        </button>
                    </div>

                    {success ? (
                        <div className="flex flex-col">
                            <div className="flex justify-center mt-6 mb-4">
                                <CheckedIcon className="w-14 h-14 text-secondary"/>
                            </div>
                            <div className="text-xl font-barlow font-normal leading-snug text-center">
                                <FormattedMessage id="editor.print.successEmail.title" />
                            </div>
                            <div className="text-center text-sm my-4">
                                <FormattedMessage 
                                    id={docTypeHandler.getTranslations().successEmailFirstLine}
                                    values={{
                                        additionalEmail: "Email here"
                                    }}
                                />
                            </div>
                            <div className="text-center text-sm my-4">
                                <FormattedMessage id="editor.print.successEmail.secondLine" />
                            </div>
                            <div className="flex justify-end mt-4">
                                <PrimaryButton onClick={closeModal}>
                                    <FormattedMessage id="editor.print.successEmail.back"/>
                                </PrimaryButton>
                            </div>
                        </div>
                    ) : data != null ? (
                        <div className="flex">
                            <div className="w-[20rem] flex-shrink-0 flex flex-col gap-4 pr-5 pl-2">
                                {additionalInfo.map(([k,v])=><div>
                                    <div className="flex justify-between">
                                        <div>{k}</div>
                                        <div className="font-bold">{v}</div>
                                    </div>
                                </div>)}
                            </div>
                            <div className="flex-auto flex flex-col flex-grow">
                                <div className="flex-auto pb-10">
                                    <div className="grid grid-cols-3 items-center gap-3">
                                        <div className="flex-auto">
                                            <FormGroup groupTitle={<FormattedMessage id="editor.print.noPrints"/>}>
                                                <FormSelect
                                                    options={numberOfPrints}
                                                    selected={data?.numberOfPrints}
                                                    onChange={value => setData(assoc('numberOfPrints', value, data))}
                                                    getLabel={(v) => v.toString()}
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="flex-auto">
                                            <FormGroup groupTitle={<FormattedMessage id="editor.print.material"/>}>
                                                <div className="flex items-center" onClick={() => setData(assoc('withLaminage', !data.withLaminage, data))}>
                                                <span className="inline-block h-4 mr-2">
                                                    <Checkbox
                                                        checked={data.withLaminage}
                                                    />
                                                </span>

                                                    <FormattedMessage id="editor.print.withLaminage"/>
                                                </div>
                                            </FormGroup>
                                        </div>
                                    </div>

                                    <FormGroup groupTitle={<FormattedMessage id="editor.print.restaurantName"/>}>
                                        <FormInput
                                            value={data.restaurantName}
                                            onChange={event => setData(assoc('restaurantName', event.target.value, data))}
                                        />
                                    </FormGroup>

                                    <FormGroup groupTitle={<FormattedMessage id="editor.print.deliveryAddress"/>}>
                                        <FormInput
                                            value={data.address}
                                            onChange={event => setData(assoc('address', event.target.value, data))}
                                        />
                                    </FormGroup>

                                    <div className="grid grid-cols-3 items-center gap-3">
                                        <div className="flex-auto">
                                            <FormGroup groupTitle={<FormattedMessage id="editor.print.zip"/>}>
                                                <FormInput
                                                    type="number"
                                                    value={data.zip.toString()}
                                                    onChange={event => setData(assoc('zip', event.target.value, data))}
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="flex-auto col-span-2">
                                            <FormGroup groupTitle={<FormattedMessage id="editor.print.city"/>}>
                                                <FormInput
                                                    value={data.city}
                                                    onChange={event => setData(assoc('city', event.target.value, data))}
                                                />
                                            </FormGroup>
                                        </div>
                                    </div>

                                    <FormGroup groupTitle={<FormattedMessage id="editor.print.deliveryDate"/>}>
                                        <FormInput
                                            value={data.date}
                                            onChange={event => setData(assoc('date', event.target.value, data))}
                                        />
                                    </FormGroup>

                                    <FormGroup groupTitle={<FormattedMessage id="editor.print.comment"/>}>
                                        <FormInput
                                            value={data.comment}
                                            onChange={event => setData(assoc('comment', event.target.value, data))}
                                        />
                                    </FormGroup>

                                    <FormGroup groupTitle={<FormattedMessage id="editor.print.language"/>}>
                                        <FormSelect
                                            options={['de', 'fr', 'it', 'en'] as PrintDataLanguageType[]}
                                            selected={data.language}
                                            onChange={value => setData(assoc('language', value, data))}
                                            getLabel={(langCode) => intl.formatMessage({id: `editor.print.language.${langCode}`})}
                                        />
                                    </FormGroup>

                                    <FormGroup groupTitle={<FormattedMessage id="editor.print.additionalEmails"/>}>
                                        <FormInput
                                            value={data.additionalEmails}
                                            hasError={validEmailRequiredError}
                                            onChange={event => setData(assoc('additionalEmails', event.target.value, data))}
                                        />
                                        {(emailRequired||validEmailRequiredError)&&<div className={`text-xs ${validEmailRequiredError?'text-primary':''}`}>
                                            {emailRequired
                                                ?<FormattedMessage id="editor.print.additionalEmails.required.SM" />
                                                :<FormattedMessage id="editor.print.additionalEmails.required" />
                                            }
                                        </div>}
                                    </FormGroup>
                                </div>

                                <div className="flex-none flex gap-2 justify-end items-center">
                                    <SecondaryButton onClick={closeModal}>
                                        <FormattedMessage id="editor.print.cancel" />
                                    </SecondaryButton>

                                    <PrimaryButton disabled={addressDataLoading || printRequestLoading || !formFilled} onClick={sendPrintRequest}>
                                        <div className="flex items-center justify-between">
                                            {printRequestLoading && <Spinner className="w-3 h-3 mr-3" />}
                                            <span>
                                            <FormattedMessage id="editor.print.send" />
                                        </span>
                                        </div>
                                    </PrimaryButton>
                                </div>

                                {success && <div className="flex justify-end text-green text-sm">
                                <FormattedMessage id="editor.print.success" />
                                </div>}

                                {error && <div className="flex justify-end text-primary text-sm">
                                <FormattedMessage id="editor.print.error" />
                                </div>}
                            </div>
                        </div>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <Spinner className="w-5 h-5" />
                        </div>
                    )}
                </div>
            </div>
        </Dialog>
    );
}